import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const BackgroundSpawner = forwardRef(({ 
  textureFolder = 'textures/background',
  minObjects = 20,
  maxObjects = 50,
  minSize = 0.5,
  maxSize = 3.0,
  positionRange = { minX: -10, maxX: 10, minY: -10, maxY: 10, minZ: -8, maxZ: -2 },
  randomizer = false,
  isExporting = false
}, ref) => {
  const groupRef = useRef();
  const objsRef = useRef([]);
  const textureLoader = useRef(new THREE.TextureLoader());
  const availableTextures = useRef([]);
  const textureCache = useRef(new Map()); // Cache für geladene Texturen
  const preloadedTextures = useRef([]); // Array von bereits geladenen Texturen

  // Lade verfügbare Texturen und preload sie
  useEffect(() => {
    fetch(`/${textureFolder}/index.json`)
      .then(res => res.json())
      .then(data => {
        // index.json ist ein Array von Strings
        const textures = Array.isArray(data) ? data : (data.textures || []);
        availableTextures.current = textures;
        
        // Preload alle Texturen
        preloadedTextures.current = [];
        const loadPromises = textures.map(textureName => {
          return new Promise((resolve, reject) => {
            const textureUrl = `/${textureFolder}/${textureName}`;
            const texture = textureLoader.current.load(
              textureUrl,
              (loadedTexture) => {
                loadedTexture.wrapS = loadedTexture.wrapT = THREE.RepeatWrapping;
                loadedTexture.flipY = false;
                textureCache.current.set(textureUrl, loadedTexture);
                preloadedTextures.current.push(loadedTexture);
                resolve(loadedTexture);
              },
              undefined,
              (error) => {
                console.warn(`Failed to load texture: ${textureUrl}`, error);
                reject(error);
              }
            );
          });
        });
        
        Promise.allSettled(loadPromises).then(() => {
          console.log(`Preloaded ${preloadedTextures.current.length} textures for BackgroundSpawner`);
        });
      })
      .catch(err => {
        console.warn('Could not load texture index:', err);
        availableTextures.current = [];
      });
  }, [textureFolder]);

  // Spawne Objekte
  useEffect(() => {
    if (!groupRef.current) return;

    // Entferne alte Objekte
    objsRef.current.forEach(obj => {
      obj.geometry?.dispose();
      obj.material?.dispose();
      if (obj.parent) obj.parent.remove(obj);
    });
    objsRef.current = [];

    // Anzahl der Objekte
    const numObjects = Math.floor(Math.random() * (maxObjects - minObjects + 1)) + minObjects;

    // Spawne neue Objekte
    for (let i = 0; i < numObjects; i++) {
      const size = Math.random() * (maxSize - minSize) + minSize;
      const geometry = new THREE.BoxGeometry(size, size, size);
      
      // Wähle zufällige Textur aus Cache oder preloaded Texturen
      let material;
      if (preloadedTextures.current.length > 0) {
        // Verwende zufällige bereits geladene Textur
        const randomTexture = preloadedTextures.current[
          Math.floor(Math.random() * preloadedTextures.current.length)
        ];
        material = new THREE.MeshStandardMaterial({ map: randomTexture });
      } else if (availableTextures.current.length > 0) {
        // Fallback: Lade Textur synchron (sollte nicht passieren wenn Preloading funktioniert)
        const randomTextureIndex = Math.floor(Math.random() * availableTextures.current.length);
        const textureUrl = `/${textureFolder}/${availableTextures.current[randomTextureIndex]}`;
        
        // Prüfe Cache zuerst
        let texture = textureCache.current.get(textureUrl);
        if (!texture) {
          texture = textureLoader.current.load(textureUrl);
          texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
          texture.flipY = false;
          textureCache.current.set(textureUrl, texture);
        }
        
        material = new THREE.MeshStandardMaterial({ map: texture });
      } else {
        material = new THREE.MeshStandardMaterial({ 
          color: `hsl(${Math.random() * 360}, 50%, 50%)` 
        });
      }

      const cube = new THREE.Mesh(geometry, material);
      
      // Zufällige Position
      cube.position.set(
        Math.random() * (positionRange.maxX - positionRange.minX) + positionRange.minX,
        Math.random() * (positionRange.maxY - positionRange.minY) + positionRange.minY,
        Math.random() * (positionRange.maxZ - positionRange.minZ) + positionRange.minZ
      );

      // Zufällige Rotation
      cube.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );

      groupRef.current.add(cube);
      objsRef.current.push(cube);
    }
  }, [minObjects, maxObjects, minSize, maxSize, positionRange, textureFolder]);

  // Randomisierung während Export - optimiert für Performance
  const lastRandomizedFrame = useRef(-1);
  useFrame((state) => {
    if (isExporting && randomizer && groupRef.current) {
      const currentFrame = state.clock.elapsedTime * 60; // ~60 FPS
      // Randomisiere nur einmal pro Frame (verhindert mehrfache Updates)
      if (Math.floor(currentFrame) !== lastRandomizedFrame.current) {
        lastRandomizedFrame.current = Math.floor(currentFrame);
        
        objsRef.current.forEach(cube => {
          // Neue Position
          cube.position.set(
            Math.random() * (positionRange.maxX - positionRange.minX) + positionRange.minX,
            Math.random() * (positionRange.maxY - positionRange.minY) + positionRange.minY,
            Math.random() * (positionRange.maxZ - positionRange.minZ) + positionRange.minZ
          );

          // Neue Rotation
          cube.rotation.set(
            Math.random() * Math.PI * 2,
            Math.random() * Math.PI * 2,
            Math.random() * Math.PI * 2
          );

          // Neue Größe (erstelle neue Geometry nur wenn sich Größe ändert)
          const newSize = Math.random() * (maxSize - minSize) + minSize;
          const currentSize = cube.geometry.parameters?.width || newSize;
          if (Math.abs(currentSize - newSize) > 0.1) {
            cube.geometry.dispose();
            cube.geometry = new THREE.BoxGeometry(newSize, newSize, newSize);
          }

          // Neue Textur aus Cache/preloaded Texturen (schnell, keine Ladezeit)
          if (preloadedTextures.current.length > 0) {
            const randomTexture = preloadedTextures.current[
              Math.floor(Math.random() * preloadedTextures.current.length)
            ];
            // Nur ändern wenn sich Textur ändert
            if (cube.material.map !== randomTexture) {
              cube.material.map = randomTexture;
              cube.material.needsUpdate = true;
            }
          } else if (availableTextures.current.length > 0) {
            // Fallback: Verwende Cache
            const randomTextureIndex = Math.floor(Math.random() * availableTextures.current.length);
            const textureUrl = `/${textureFolder}/${availableTextures.current[randomTextureIndex]}`;
            const cachedTexture = textureCache.current.get(textureUrl);
            
            if (cachedTexture && cube.material.map !== cachedTexture) {
              cube.material.map = cachedTexture;
              cube.material.needsUpdate = true;
            }
          }
        });
      }
    }
  });

  useImperativeHandle(ref, () => ({
    getObjects: () => objsRef.current
  }));

  return <group ref={groupRef} />;
});

BackgroundSpawner.displayName = 'BackgroundSpawner';

export default BackgroundSpawner;
